import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Eye, ShieldAlert } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import EmptyState from "@/components/jea/EmptyState";
import { centsToUSD, shortDate } from "@/lib/dates";
import { chapterInfo } from "@/lib/bankruptcy";
import { cn } from "@/lib/utils";

const HeaderCell = ({ children, className = "" }) => (
  <th className={cn("jea-caption text-text-secondary p-4 text-left", className)}>{children}</th>
);

const ChapterPill = ({ chapter }) => {
  const info = chapterInfo(chapter);
  if (!info) {
    return <span className="jea-caseno text-text-secondary text-[12px]">{chapter || "—"}</span>;
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-full text-[10px] font-bold tracking-[0.06em] uppercase"
      style={{ background: `${info.color}22`, border: `1px solid ${info.color}66`, color: info.color }}
    >
      {info.label}
    </span>
  );
};

const ActiveFilingsTable = ({ filings, onMarkReviewed, marking }) => {
  const nav = useNavigate();
  if (!filings || filings.length === 0) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="No active filings"
        description="Great news — no judgments in your portfolio currently have an active bankruptcy stay. This page will alert you the moment a PACER sweep detects one."
        testid="active-filings-empty"
      />
    );
  }

  return (
    <JeaCard className="p-0 overflow-hidden" data-testid="active-filings-table">
      <div className="px-5 py-4 border-b border-border-default flex items-center justify-between">
        <div>
          <div className="jea-caption">Enforcement HALT</div>
          <h2 className="text-white font-semibold text-[16px] mt-0.5">
            {filings.length} Active Bankruptcy Filing{filings.length === 1 ? "" : "s"}
          </h2>
        </div>
        <div className="text-[12px] text-text-secondary">
          Enforcement is legally prohibited on any row shown here.
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-default bg-bg-primary/40">
              <HeaderCell>Debtor</HeaderCell>
              <HeaderCell>Case Number</HeaderCell>
              <HeaderCell>Chapter</HeaderCell>
              <HeaderCell>Filed</HeaderCell>
              <HeaderCell>Court</HeaderCell>
              <HeaderCell>Judgment</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell className="text-right">Actions</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {filings.map((f, i) => {
              const reviewed = !!f.reviewed;
              const isMarking = marking === `${f.debtor_id}:${f.case_number}`;
              return (
                <motion.tr
                  key={`${f.debtor_id}:${f.case_number}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: i * 0.03 }}
                  className={cn(
                    "border-b border-border-default hover:bg-bg-tertiary transition-colors",
                    !reviewed && "bg-danger/5",
                  )}
                  data-testid={`active-filing-row-${f.debtor_id}`}
                >
                  <td className="p-4">
                    <button
                      type="button"
                      onClick={() => nav(`/debtors/${f.debtor_id}`)}
                      className="text-white font-semibold hover:text-accent-pink transition-colors text-left"
                      data-testid={`active-filing-name-${f.debtor_id}`}
                    >
                      {f.debtor_name}
                    </button>
                  </td>
                  <td className="p-4 jea-caseno text-[13px]">{f.case_number}</td>
                  <td className="p-4"><ChapterPill chapter={f.chapter} /></td>
                  <td className="p-4 jea-date text-[13px]">{shortDate(f.filed_date)}</td>
                  <td className="p-4 text-text-secondary text-[13px]">{f.court || "—"}</td>
                  <td className="p-4 jea-mono text-accent-pink font-bold text-[13px]">
                    {centsToUSD(f.judgment_amount_cents || 0)}
                  </td>
                  <td className="p-4">
                    {reviewed ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase bg-success/15 text-success border border-success/40"
                        data-testid={`active-filing-reviewed-${f.debtor_id}`}
                      >
                        <CheckCircle2 size={11} /> Reviewed
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase bg-danger/15 text-danger border border-danger/40 animate-pulse-alert"
                        data-testid={`active-filing-unreviewed-${f.debtor_id}`}
                      >
                        Needs Review
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2 justify-end">
                      {f.report_id && (
                        <button
                          type="button"
                          onClick={() => nav(`/debtors/${f.debtor_id}/reports/${f.report_id}`)}
                          className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/60 transition-colors inline-flex items-center justify-center"
                          title="View report"
                          data-testid={`active-filing-view-${f.debtor_id}`}
                        >
                          <Eye size={14} />
                        </button>
                      )}
                      {!reviewed && (
                        <button
                          type="button"
                          disabled={isMarking}
                          onClick={() => onMarkReviewed(f)}
                          className="h-8 px-3 rounded-[6px] border border-accent-pink text-accent-pink hover:bg-accent-pink hover:text-[#091424] transition-colors text-[12px] font-semibold disabled:opacity-50"
                          data-testid={`active-filing-mark-reviewed-${f.debtor_id}`}
                        >
                          {isMarking ? "Marking…" : "Mark Reviewed"}
                        </button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </JeaCard>
  );
};

export default ActiveFilingsTable;
